// @flow
import type { AnyAction } from 'react-redux';
import { createReducer } from 'redux-act';

import { createSelector } from 'reselect';

import type { Catalog } from '@gandi/cart';
import {
  createSequoiaResource,
  type FetchResponse,
  type SequoiaResource,
  type SequoiaResourceType,
} from '@gandi/resource/redux';

import type { FullState } from 'src/types';

import type { DomainTransferBulkState } from '../types';

const initialState: DomainTransferBulkState = {
  transferable: [],
  transfer_error: [],
  creatable: [],
  unavailable: [],
  catalog: {},
};

type BulkResourceType = SequoiaResourceType<{
  postPayload: string,
  PostResponse: FetchResponse<200, DomainTransferBulkState>,
}>;
export type BulkResource = SequoiaResource<BulkResourceType>;

export const bulkResource: BulkResource = createSequoiaResource(
  'cart',
  'bulk',
  {
    postPath: '/suggest/cart/{cart_uuid}/bulk/transferin',
  },
);

// Selector
export const selectBulkResponse = (state: FullState): DomainTransferBulkState =>
  state.domainTransferBulk;

export const selectCountBulkResults: (state: FullState) => number =
  createSelector(selectBulkResponse, results => {
    if (!results) return 0;
    return (
      (results.unavailable?.length || 0) +
      (results.creatable?.length || 0) +
      (results.transferable?.length || 0) +
      (results.transfer_error?.length || 0)
    );
  });

export const selectCatalogPriceByFqdnAndProcess = (
  state: FullState,
  {
    fqdn,
    process,
  }: {
    fqdn: string,
    process: 'create' | 'renew' | 'transfer',
  },
): Catalog | void => {
  const catalog = state.domainTransferBulk.catalog;
  const parts = fqdn.split('.');

  const foundDomainKey = parts
    .map((_, i) => {
      const key = parts.slice(i).join('.');
      return i === 0
        ? // FQDN catalog
          key
        : // TLD catalog
          `.${key}`;
    })
    .find(key => catalog[key]);

  const domainCatalog = foundDomainKey ? catalog[foundDomainKey] : undefined;
  if (!domainCatalog) return undefined;

  const processFqdnPrices = domainCatalog[process];
  if (!processFqdnPrices) return undefined;

  return processFqdnPrices.prices[0];
};

// Reducer
const bulkHandler = (
  state: DomainTransferBulkState,
  { response }: { response: FetchResponse<number, DomainTransferBulkState> },
): DomainTransferBulkState => response.data || state;

export default (createReducer<DomainTransferBulkState, AnyAction>(
  {
    [bulkResource.post.success.getType()]: bulkHandler,
  },
  initialState,
): any);
